import { call, select, takeLatest } from 'redux-saga/effects';
import * as api from '@api/qcQueryManagement';
import { runToast, showErrorToast } from '../tools';
import { actions } from './qcQueries.slice';
import { Resp, dispatch } from '../helpers';
import { PayloadAction } from '@reduxjs/toolkit';
import { selectClinicalTrialContextState } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { QueryTicketMessageRequest } from '@restore/queries/queries.types';
import {
  QCQueriesFilters,
  QCQueryFetchAction,
  UpdateCurrentQueryAction,
} from './qcQueries.types';
import { RootState } from '@restore/store';
import { PaginationAndSortType } from '@restore/paginationAndSort/paginationAndSort.types';

export function* fetchQCQueriesWorker(action: PayloadAction<string>) {
  try {
    const localFilters = (yield select(
      (state: RootState) => state.qcQueries?.filters || {}
    )) as QCQueriesFilters;
    const pagSort = (yield select(
      (state: RootState) =>
        state.paginationAndSort?.lists?.qcQueries || {
          currentPage: 1,
          currentPageSize: 10,
        }
    )) as PaginationAndSortType;
    const filters = {
      ...localFilters,
      page: pagSort.currentPage,
      size: pagSort.currentPageSize,
      sort: pagSort.sortBy,
    };
    const response: Resp<typeof api.getQCQueries> = yield call(
      api.getQCQueries,
      action.payload,
      filters
    );
    yield dispatch(actions.fetchQCQueriesSuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch queries');
    yield dispatch(actions.fetchQCQueriesFailure(error as Error));
  }
}

export function* fetchCurrentQueryWorker(action: QCQueryFetchAction) {
  try {
    const { queryId, clinicalTrialId } = action.payload;
    const response: Resp<typeof api.getQCQueryById> = yield call(
      api.getQCQueryById,
      queryId,
      clinicalTrialId
    );
    yield dispatch(actions.fetchCurrentQuerySuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch query');
    yield dispatch(actions.fetchCurrentQueryFailure(error as Error));
  }
}

export function* updateCurrentQueryWorker(action: UpdateCurrentQueryAction) {
  try {
    const { queryId, clinicalTrialId, updateData, refreshList } =
      action.payload;
    yield call(api.updateQCQueryById, queryId, clinicalTrialId, updateData);
    const fetchResponse: Resp<typeof api.getQCQueryById> = yield call(
      api.getQCQueryById,
      queryId,
      clinicalTrialId
    );
    yield dispatch(actions.updateCurrentQuerySuccess(fetchResponse.data));
    if (refreshList) {
      yield dispatch(actions.fetchQCQueries(clinicalTrialId));
    }
    yield call(runToast, 'success', 'Query updated successfully', 'Success');
  } catch (error) {
    yield* showErrorToast(error, 'Failed to update query');
    yield dispatch(actions.updateCurrentQueryFailure(error as Error));
  }
}

export function* createQCQueryWorker(
  action: PayloadAction<QueryTicketMessageRequest>
) {
  try {
    const { data }: ReturnType<typeof selectClinicalTrialContextState> =
      yield select(selectClinicalTrialContextState);
    const clinicalTrialId = data?.configuration.id ?? '';

    yield call(api.createQuery, clinicalTrialId, action.payload);
    yield dispatch(actions.createQCQuerySuccess(null));
    yield dispatch(actions.fetchQCQueries(clinicalTrialId));
    yield call(runToast, 'success', 'Query created successfully', 'Success');
  } catch (error) {
    yield* showErrorToast(error, 'Failed to create query');
    yield dispatch(actions.createQCQueryFailure(error as Error));
  }
}

export function* fetchQCQueryRecipientsWorker(action: PayloadAction<string>) {
  try {
    const response: Resp<typeof api.getQCQueryRecipients> = yield call(
      api.getQCQueryRecipients,
      action.payload
    );
    yield dispatch(actions.fetchQCQueryRecipientsSuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch query recipients');
    yield dispatch(actions.fetchQCQueryRecipientsFailure(error as Error));
  }
}

export function* qcQueriesSaga() {
  yield takeLatest(actions.fetchQCQueries.type, fetchQCQueriesWorker);
  yield takeLatest(actions.fetchCurrentQuery.type, fetchCurrentQueryWorker);
  yield takeLatest(actions.updateCurrentQuery.type, updateCurrentQueryWorker);
  yield takeLatest(actions.createQCQuery.type, createQCQueryWorker);
  yield takeLatest(
    actions.fetchQCQueryRecipients.type,
    fetchQCQueryRecipientsWorker
  );
}
